import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { PageHeader } from '@/components/layout';
import { EmptyState, LoadingState } from '@/components/data-display';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Search,
  Plus,
  QrCode,
  MoreHorizontal,
  Eye,
  Pencil,
  Copy,
  Trash2,
  Download,
  ChevronDown,
  Check,
  BarChart3,
  Archive,
  Play,
  Pause,
  Grid3X3,
  List,
  ArrowUpDown,
  Activity,
  Zap,
  Filter,
  ScanLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiV1 } from '@/api/client';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'archived', label: 'Archived' },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'scans_desc', label: 'Most Scans' },
  { value: 'scans_asc', label: 'Fewest Scans' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
];

const VIEW_MODES = [
  { value: 'table', icon: List },
  { value: 'grid', icon: Grid3X3 },
];

const PER_PAGE = 50;

// Frontend sort labels → backend (sort_by, sort_order) tuple.
const SORT_MAP = {
  newest:     ['created_at', 'desc'],
  oldest:     ['created_at', 'asc'],
  name_asc:   ['name', 'asc'],
  name_desc:  ['name', 'desc'],
  scans_desc: ['total_scans', 'desc'],
  scans_asc:  ['total_scans', 'asc'],
};

export default function QRList() {
  const [qrCodes, setQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: PER_PAGE, has_more: false });
  const [page, setPage] = useState(1);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('table');
  const [activeTab, setActiveTab] = useState('all');

  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);

  // Reset to page 1 whenever a filter/search that changes the result set changes.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  // Fetch QR codes from API
  const fetchQRCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('per_page', String(PER_PAGE));
      if (debouncedSearchQuery.trim()) {
        params.append('search', debouncedSearchQuery.trim());
      }
      // The activeTab is the effective status filter — 'all' shows everything.
      const effectiveStatus = activeTab !== 'all' ? activeTab : (statusFilter !== 'all' ? statusFilter : null);
      if (effectiveStatus) params.append('status', effectiveStatus);
      const [sortField, sortDir] = SORT_MAP[sortBy] || SORT_MAP.newest;
      params.append('sort_by', sortField);
      params.append('sort_order', sortDir);

      const response = await apiV1.get(`/qrcodes/?${params.toString()}`);
      const data = response.data;

      if (data.success) {
        const items = (data.data?.items || data.data || []).map(qr => ({
          id: qr.id,
          name: qr.name,
          status: qr.status || 'active',
          // Every code is a tracked /r/ redirect; what differs is who made it.
          source: qr.created_via === 'api_key' ? (qr.api_key_name || 'Integration') : 'LinkHub',
          scans: qr.total_scans || 0,
          uniqueScans: qr.unique_scans || 0,
          created: qr.created_at ? new Date(qr.created_at).toLocaleDateString() : '',
          createdAt: qr.created_at,
          url: qr.destination_url,
          qr_url: qr.qr_url,
          short_code: qr.short_code,
          tracking: qr.tracking || {}
        }));
        setQRCodes(items);
        setPagination(data.data?.pagination || data.pagination || { total: items.length, page, per_page: PER_PAGE, has_more: false });
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      toast.error('Failed to load QR codes');
      setQRCodes([]);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearchQuery, statusFilter, activeTab, sortBy]);

  useEffect(() => {
    fetchQRCodes();
  }, [fetchQRCodes]);

  // Search, status, and sort are handled server-side. (The old client-side
  // "Static / Dynamic" type filter is gone: every code is a tracked,
  // editable redirect, and it only ever filtered the current page.)
  const filteredQRs = qrCodes;

  // Stats come from the server: the list above is one page, so counting it
  // would cap every card at PER_PAGE.
  const [stats, setStats] = useState({ total: 0, activeCount: 0, pausedCount: 0, totalScans: 0, uniqueScans: 0 });

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiV1.get('/qrcodes/stats');
      if (response.data?.success) {
        const d = response.data.data || {};
        setStats({
          total: d.total || 0,
          activeCount: d.active || 0,
          pausedCount: d.paused || 0,
          totalScans: d.total_scans || 0,
          uniqueScans: d.unique_scans || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching QR stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const resultCount = pagination.total ?? filteredQRs.length;

  // After a change: reload the current page and the headline counts.
  const refresh = () => {
    fetchQRCodes();
    fetchStats();
  };

  // Handle status change
  const handleStatusChange = async (id, newStatus) => {
    try {
      await apiV1.put(`/qrcodes/${id}`, { status: newStatus });
      toast.success(`QR code ${newStatus === 'active' ? 'activated' : newStatus === 'paused' ? 'paused' : 'updated'}`);
      refresh();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to archive this QR code?')) return;
    
    try {
      await apiV1.delete(`/qrcodes/${id}`);
      toast.success('QR code archived');
      refresh();
    } catch (error) {
      toast.error('Failed to delete QR code');
    }
  };

  // Handle duplicate
  const handleDuplicate = async (id) => {
    try {
      await apiV1.post(`/qrcodes/${id}/duplicate`);
      toast.success('QR code duplicated');
      refresh();
    } catch (error) {
      toast.error('Failed to duplicate QR code');
    }
  };

  // Handle bulk actions
  const handleBulkStatusChange = async (newStatus) => {
    try {
      await Promise.all(selectedIds.map(id => apiV1.put(`/qrcodes/${id}`, { status: newStatus })));
      toast.success(`${selectedIds.length} QR codes updated`);
      setSelectedIds([]);
      refresh();
    } catch (error) {
      toast.error('Failed to update QR codes');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to archive ${selectedIds.length} QR codes?`)) return;
    
    try {
      await Promise.all(selectedIds.map(id => apiV1.delete(`/qrcodes/${id}`)));
      toast.success(`${selectedIds.length} QR codes archived`);
      setSelectedIds([]);
      refresh();
    } catch (error) {
      toast.error('Failed to delete QR codes');
    }
  };

  const allSelected = filteredQRs.length > 0 && selectedIds.length === filteredQRs.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < filteredQRs.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQRs.map((qr) => qr.id));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { variant: 'default', icon: Check, className: 'bg-green-500/10 text-green-600 border-green-200' },
      draft: { variant: 'secondary', icon: null },
      archived: { variant: 'outline', icon: Archive },
      paused: { variant: 'secondary', icon: Pause, className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
    };
    const { className } = config[status] || config.draft;
    return (
      <Badge variant="outline" className={className}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="qr-list-page">
      <PageHeader
        title="QR Codes"
        description="Manage all your QR codes in one place"
        actions={
          <Button asChild data-testid="qr-create-start-button">
            <Link to="/qrs/new">
              <Plus className="h-4 w-4 mr-2" />
              Create QR Code
            </Link>
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <QrCode className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-muted-foreground">Total QR Codes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Activity className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeCount}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <ScanLine className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalScans.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Total Scans</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Zap className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.uniqueScans.toLocaleString()}</p>
                <p className="text-xs text-muted-foreground">Unique Scans</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Status */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all" className="flex items-center gap-2">
            <QrCode className="w-4 h-4" />
            All ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="active" className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Active ({stats.activeCount})
          </TabsTrigger>
          <TabsTrigger value="paused" className="flex items-center gap-2">
            <Pause className="w-4 h-4" />
            Paused ({stats.pausedCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search and Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, URL, agent, region..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="qr-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[150px]" data-testid="sort-select">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex border rounded-md">
            {VIEW_MODES.map(mode => (
              <Button
                key={mode.value}
                variant={viewMode === mode.value ? 'secondary' : 'ghost'}
                size="icon"
                onClick={() => setViewMode(mode.value)}
                className="rounded-none first:rounded-l-md last:rounded-r-md"
                data-testid={`view-${mode.value}`}
              >
                <mode.icon className="w-4 h-4" />
              </Button>
            ))}
          </div>
          {selectedIds.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="bulk-actions-button">
                  Bulk Actions
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('active')} data-testid="qr-bulk-activate-button">
                  <Play className="h-4 w-4 mr-2" />
                  Activate ({selectedIds.length})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('paused')}>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause ({selectedIds.length})
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('archived')}>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive ({selectedIds.length})
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive" onClick={handleBulkDelete} data-testid="qr-bulk-delete-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedIds.length})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Results count */}
      {searchQuery && (
        <p className="text-sm text-muted-foreground">
          Found {resultCount} result{resultCount !== 1 ? 's' : ''} for "{searchQuery}"
        </p>
      )}

      {/* Table or Grid View */}
      {loading ? (
        <LoadingState rows={5} />
      ) : filteredQRs.length === 0 ? (
        <EmptyState
          icon={QrCode}
          title={searchQuery || activeTab !== 'all' ? 'No matching QR codes' : 'No QR codes yet'}
          description={searchQuery || activeTab !== 'all'
            ? 'Try adjusting your search or filters'
            : 'Create your first QR code to get started'
          }
          actionLabel={!searchQuery && activeTab === 'all' ? 'Create QR Code' : undefined}
          onAction={!searchQuery && activeTab === 'all' ? () => window.location.href = '/qrs/new' : undefined}
        />
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="qr-grid">
          {filteredQRs.map((qr) => (
            <Card key={qr.id} className="hover:shadow-md transition-shadow" data-testid={`qr-card-${qr.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 bg-muted rounded-lg">
                    <QrCode className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to={`/qrs/${qr.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/qrs/${qr.id}?edit=1`} data-testid={`edit-details-${qr.id}`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit details
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to={`/editor?qr=${qr.id}`}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit design
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDuplicate(qr.id)}>
                        <Copy className="h-4 w-4 mr-2" />
                        Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(qr.id)}>
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <h3 className="font-medium truncate mb-1">{qr.name}</h3>
                <p className="text-xs text-muted-foreground truncate mb-3">{qr.url}</p>
                <div className="flex items-center justify-between">
                  {getStatusBadge(qr.status)}
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <ScanLine className="w-4 h-4" />
                    {qr.scans.toLocaleString()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="border rounded-lg" data-testid="qr-list-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    data-testid="qr-select-all-checkbox"
                    {...(someSelected ? { 'data-state': 'indeterminate' } : {})}
                  />
                </TableHead>
                <TableHead>QR Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="text-right">Scans</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredQRs.map((qr) => (
                <TableRow key={qr.id} data-testid={`qr-row-${qr.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.includes(qr.id)}
                      onCheckedChange={() => toggleOne(qr.id)}
                      aria-label={`Select ${qr.name}`}
                      data-testid="qr-row-checkbox"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <QrCode className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="font-medium">{qr.name}</div>
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {qr.url}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(qr.status)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {qr.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    <Link to={`/analytics/${qr.id}`} className="flex items-center justify-end gap-2 hover:text-primary transition-colors">
                      {qr.scans.toLocaleString()}
                      <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{qr.created}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/qrs/${qr.id}`}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/analytics/${qr.id}`}>
                            <BarChart3 className="h-4 w-4 mr-2" />
                            View Analytics
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/qrs/${qr.id}?edit=1`} data-testid={`edit-details-row-${qr.id}`}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit details
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/editor?qr=${qr.id}`}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit design
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(qr.id)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {qr.status === 'active' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(qr.id, 'paused')}>
                            <Pause className="h-4 w-4 mr-2" />
                            Pause
                          </DropdownMenuItem>
                        )}
                        {qr.status === 'paused' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(qr.id, 'active')}>
                            <Play className="h-4 w-4 mr-2" />
                            Activate
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(qr.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {(pagination.total > 0 || qrCodes.length > 0) && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground" data-testid="pagination-summary">
            Page {pagination.page || page} of {Math.max(1, Math.ceil((pagination.total || 0) / (pagination.per_page || PER_PAGE)))} — {pagination.total || qrCodes.length} total
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  data-testid="pagination-prev"
                  aria-disabled={page <= 1}
                  onClick={(e) => {
                    e.preventDefault();
                    if (page > 1) setPage(p => p - 1);
                  }}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive onClick={(e) => e.preventDefault()}>
                  {pagination.page || page}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  data-testid="pagination-next"
                  aria-disabled={!pagination.has_more}
                  onClick={(e) => {
                    e.preventDefault();
                    if (pagination.has_more) setPage(p => p + 1);
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
